<template>
  <screen
    class="scan-container"
    :can-skip="false"
    :can-leave="state === 0"
    :screen-info="info"
    :error="error && state === 0"
    @leave="leaveScan"
  >
    <force-scan v-show="canForceScan" @click="startScanProcess" />
    <feet v-if="showFeet" class="feet-pos" />
    <raise-elbow v-if="showRaiseElbow" />
    <doll
      v-show="!showFeet && !showRaiseElbow"
      :show-clench-hint.sync="showClenchHint"
      :clench-hint-duration="3"
      :left="left"
      :right="right"
      :left-range="leftRange"
      :right-range="rightRange"
      :show-bubbles="state < 1"
      :scan-ongoing="state > 0 && !scanCompleted"
      :pose="pose"
      left-breathing
      right-breathing
      @left-hit="handleLeftHit"
      @right-hit="handleRightHit"
    />
    <countdown v-if="countdownTimer > 0" :time="countdownTimer" />
    <scan-progress
      v-if="scanProgress > -1 && !scanCompleted"
      class="scan-progress"
      :progress="scanProgress"
    />
    <scan-complete v-show="scanCompleted" />
  </screen>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import audioPlayer from '@/mixins/audio-player';
import brandMode from '@/mixins/brand-mode';
import scanBody from '@/mixins/scan-body';

import Feet from '@/components/Animations/FeetPos.vue';
import RaiseElbow from '@/components/Animations/RaiseElbow.vue';
import ForceScan from '@/components/common/ForceScan.vue';
import ScanComplete from '../components/ScanBody/ScanComplete.vue';
import Screen from '../components/common/Screen.vue';
import Doll from '../components/common/Doll.vue';
import ScanProgress from '../components/common/ScanProgress.vue';
import Countdown from '../components/common/Countdown.vue';
import ScanProcess from '../modules/scan-process';

export default {
  name: 'ScanBody',
  components: {
    Screen,
    ForceScan,
    Doll,
    ScanProgress,
    ScanComplete,
    Countdown,
    Feet,
    RaiseElbow,
  },
  mixins: [audioPlayer, scanBody, brandMode],
  mounted() {
    this.start();
  },
  data: vm => ({
    // data() runs before computed, so the brand is read from the store here
    info: vm.$store.getters['app/isSanne'] ? vm.$t('sanne.feet_pos') : vm.$t('scan.feet_pos'),
    showFeet: false,
    showRaiseElbow: false,
    hasBegunScanProcess: false,
  }),
  methods: {
    reset() {
      this.state = 0;
      this.countdownTimer = 0;
      this.scanProgress = -1;
      this.scanCompleted = false;
      this.showClenchHint = false;
      this.error = false;
      this.hasBegunScanProcess = false;
    },
    start() {
      this.toggleArmMotionListening(false);
      if (this.stepTwo) {
        this.startListen();
        this.startArmDetection();
      }
      if (this.skipTutorial) {
        this.readyToScan();
        return;
      }
      this.info = this.feetPosText;
      this.showFeet = true;
      const audioKey = this.stepTwo ? 'foot_position_feet' : '9-Position_your_feet';
      this.playAndWait(audioKey, 1, true).then(() => {
        this.showFeet = false;
      });
    },
    readyToScan() {
      this.toggleArmMotionListening(true);
      this.info = this.raiseBothArmText;
      this.playAndWait('11-Hold_fists_in_bubbles', 2, true).then(() => {
        if (this.state > 0) return;

        if (this.bothLow) {
          this.showError(this.$t('scan.raise_fists'));
          this.playAudio('Error-Fists_too_low', true);
        } else if (this.bothHigh) {
          this.showError(this.$t('scan.lower_fists'));
          this.playAudio('Error-Fists_too_high', true);
        }
      });
    },
    async startScanProcess() {
      this.hasBegunScanProcess = true;
      try {
        this.playAndWait('12-Keep_body_still', 0.5, true).then(() =>
          this.playAudio('13-Scanning_in_progress', true)
        );
        await this.startScan(true);
        await this.playAndWait('14-Scan_complete', 1, true);
        await this.finish();
      } catch (e) {
        console.log(`[ERROR] scan failed! ${e.message}`);
        this.$events.alert(this.$t('welcome.unexpected_error'));
        this.leave();
      }
    },
    leaveScan() {
      console.log(`user leave${this.stepTwo ? '' : ' s2'}`);
      ScanProcess.reportEvent(`user_leave${this.stepTwo ? '' : ' _s2'}`);
      this.leave();
    },
  },
  computed: {
    ...mapGetters('app', ['isDailyBelle']),
    ...mapState('mtmUser', ['skipTutorial']),
    feetPosText() {
      if (this.isSanne) return this.$t('sanne.feet_pos');
      return this.$t('scan.feet_pos');
    },
    raiseBothArmText() {
      if (this.isSanne) return this.$t('sanne.raise_both_arm');
      return this.$t('scan.raise_both_arm');
    },
    scanInProgressText() {
      if (this.isSanne) return this.$t('sanne.scan_in_progress');
      return this.$t('scan.scan_in_progress');
    },
    screenName() {
      if (!this.showIntro) return '';

      if (this.stepTwo) return this.$t('scan.begin_2nd');

      return this.$t('scan.step_name');
    },
    canForceScan() {
      return this.state === 0 && !this.showClenchHint && !this.showFeet;
    },
    pose() {
      if (this.stepTwo && this.isDailyBelle) return 'elbow';
      return 'default';
    },
  },
  watch: {
    stepTwo(nv) {
      if (nv) {
        this.reset();
        console.log('begin phase two scan');
        this.start();
      }
    },
    state(nv) {
      if (nv === 1) {
        this.info = this.$t('scan.scan_keep_still');
      }
    },
    countdownTimer(nv, ov) {
      if (nv === 0 && ov === 1) {
        this.info = this.scanInProgressText;
      }
    },
    showFeet(nv, ov) {
      if (!nv && ov) {
        if (this.pose === 'elbow') {
          // force arm range to 150-200 mm
          this.$events.emit('hardware.onBluetoothData', { data: [150, 200, 150, 200] });
          this.info = this.$t('scan.raise_elbow');
          this.playAudio('keep_elbows_outward', true).then(() => {
            this.showRaiseElbow = false;
            this.readyToScan();
          });
          this.showRaiseElbow = true;
          return;
        }
        this.info = this.$t('scan.clench');
        this.playAudio('10-Clench_fingers');
        this.showClenchHint = true;
      }
    },
    showClenchHint(nv, ov) {
      if (!nv && ov) {
        this.readyToScan();
      }
    },
    somethingHit(nv) {
      this.error = !nv;
    },
    bothHit(nv) {
      this.info = nv ? this.$t('scan.hold_still') : this.raiseBothArmText;
    },
    async bothReady(nv) {
      if (nv && !this.hasBegunScanProcess) {
        await this.startScanProcess();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.scan-container {
  ::v-deep {
    .content-container {
      @apply relative;
    }
    .complete-wrapper {
      top: 0;
      bottom: 0;
      transform: translateY(-6%);
    }
  }
}
</style>
